// src/pages/BusinessCasePage.tsx
import React, { useState } from "react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell,
} from "recharts";
import { TrendingUp, DollarSign, Users, Zap, Building2, ArrowRight, CheckCircle, XCircle } from "lucide-react";
import { clsx } from "clsx";
import { LOYALTY_SUMMARIES, RETAILERS } from "@/data";
import { intentCategories } from "@/data/intent";

type Tab = "market" | "revenue" | "partners" | "flywheel" | "identity";

// ── Market verticals ──────────────────────────────────────────
const VERTICALS = [
  { name: "Grocery & Mass Retail",       partners: 15, avgSpend: 8_000_000,  category: "Food & Beverage",        signals: 713_755_212 },
  { name: "Home Improvement & Hardware", partners: 8,  avgSpend: 12_000_000, category: "Consumer Goods",         signals: 373_301_847 },
  { name: "Auto Parts & Accessories",    partners: 10, avgSpend: 6_000_000,  category: "Auto",                   signals: 806_720_984 },
  { name: "Consumer Electronics",        partners: 12, avgSpend: 10_000_000, category: "Consumer Electronics",   signals: 491_731_471 },
  { name: "Fashion & Apparel",           partners: 20, avgSpend: 5_000_000,  category: "Apparel & Accessories",  signals: 216_000_000 },
  { name: "Health & Wellness",           partners: 10, avgSpend: 4_000_000,  category: "Health & Wellness",      signals: 123_000_000 },
];

// ── Flywheel stages ───────────────────────────────────────────
const FLYWHEEL = [
  { id: 1, label: "New Partner Joins",       desc: "Brings loyalty & transaction data",                         color: "#1E2761", angle: 270 },
  { id: 2, label: "MRN Identity Resolution", desc: "Match rates increase for ALL partners as graph grows",      color: "#3056D3", angle: 330 },
  { id: 3, label: "Intent Enrichment",       desc: "Every record enriched with 8,305 daily-refreshed topics",  color: "#0891B2", angle: 30  },
  { id: 4, label: "Campaigns Activate",      desc: "Media dollars flow through the MRN platform",              color: "#06b6d4", angle: 90  },
  { id: 5, label: "Attribution Proves ROI",  desc: "Closed-loop measurement validates every dollar",           color: "#10b981", angle: 150 },
  { id: 6, label: "Partner Increases Spend", desc: "Proven ROI drives budget expansion",                       color: "#059669", angle: 210 },
];

// ── Network density data ──────────────────────────────────────
const NETWORK_DATA = [
  { partners: 3,   matchRate: 88, roas: 4.2 },
  { partners: 10,  matchRate: 92, roas: 5.1 },
  { partners: 25,  matchRate: 95, roas: 5.8 },
  { partners: 50,  matchRate: 97, roas: 6.3 },
  { partners: 100, matchRate: 98, roas: 6.7 },
];

const PIE_COLORS = ["#1E2761","#3056D3","#0891B2","#10b981","#f59e0b"];

function fmt(n: number, type: "currency" | "compact" | "percent" | "number" = "number"): string {
  if (type === "percent")  return `${n.toFixed(1)}%`;
  if (type === "currency") {
    if (n >= 1_000_000_000) return `$${(n/1_000_000_000).toFixed(1)}B`;
    if (n >= 1_000_000)     return `$${(n/1_000_000).toFixed(1)}M`;
    if (n >= 1_000)         return `$${(n/1_000).toFixed(0)}K`;
    return `$${n.toFixed(0)}`;
  }
  if (n >= 1_000_000_000) return `${(n/1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000)     return `${(n/1_000_000).toFixed(1)}M`;
  if (n >= 1_000)         return `${(n/1_000).toFixed(0)}K`;
  return n.toLocaleString();
}

// ── Slider ────────────────────────────────────────────────────
function Slider({ label, value, min, max, step, format, onChange }: {
  label: string; value: number; min: number; max: number; step: number;
  format: "currency"|"percent"|"number"; onChange: (v: number) => void;
}) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="space-y-1">
      <div className="flex justify-between items-center">
        <span className="text-xs text-slate">{label}</span>
        <span className="text-xs font-bold text-navy tabular">{fmt(value, format)}</span>
      </div>
      <div className="relative h-4 flex items-center">
        <div className="w-full h-1 bg-gray-100 rounded-full">
          <div className="h-full bg-blue rounded-full" style={{ width: `${pct}%` }} />
        </div>
        <input type="range" min={min} max={max} step={step} value={value}
          onChange={e => onChange(Number(e.target.value))}
          className="absolute inset-0 w-full opacity-0 cursor-pointer" />
        <div className="absolute w-3 h-3 rounded-full bg-white border-2 border-blue shadow pointer-events-none"
          style={{ left: `calc(${pct}% - 6px)` }} />
      </div>
    </div>
  );
}

export default function BusinessCasePage() {
  const [tab, setTab] = useState<Tab>("market");

  // Revenue simulator state
  const [loyaltyCount,   setLoyaltyCount]   = useState(5);
  const [loyaltyACV,     setLoyaltyACV]     = useState(300_000);
  const [activationCount,setActivationCount]= useState(3);
  const [activationSpend,setActivationSpend]= useState(2_000_000);
  const [activationFee,  setActivationFee]  = useState(10);
  const [enterpriseCount,setEnterpriseCount]= useState(2);
  const [enterpriseACV,  setEnterpriseACV]  = useState(1_500_000);
  const [suppCount,      setSuppCount]      = useState(5);
  const [suppACV,        setSuppACV]        = useState(100_000);
  const [wlCount,        setWlCount]        = useState(0);
  const [wlACV,          setWlACV]          = useState(5_000_000);
  const [ooUniques]                         = useState(90_000_000);
  const [ooCPM,          setOoCPM]          = useState(18);
  const [ooSellThrough,  setOoSellThrough]  = useState(60);

  // Revenue calculations
  const loyaltyRev    = loyaltyCount    * loyaltyACV;
  const activationRev = activationCount * activationSpend * (activationFee / 100);
  const enterpriseRev = enterpriseCount * enterpriseACV;
  const suppRev       = suppCount       * suppACV;
  const wlRev         = wlCount         * wlACV;
  const ooRev         = (ooUniques / 1000) * ooCPM * (ooSellThrough / 100) * 12;
  const totalARR      = loyaltyRev + activationRev + enterpriseRev + suppRev + wlRev + ooRev;

  const revenueStreams = [
    { name: "Loyalty Intelligence",   value: loyaltyRev,    color: "#1E2761" },
    { name: "Full-Funnel Activation", value: activationRev, color: "#3056D3" },
    { name: "Enterprise Suite",       value: enterpriseRev, color: "#0891B2" },
    { name: "Suppression-Only",       value: suppRev,       color: "#06b6d4" },
    { name: "White-Label RMN",        value: wlRev,         color: "#10b981" },
    { name: "O&O Media",              value: ooRev,         color: "#f59e0b" },
  ];

  const yearlyProjection = [1, 2, 3].map(yr => ({
    year: `Year ${yr}`,
    arr:  Math.round(totalARR * Math.pow(1.6, yr - 1) / 1_000_000),
  }));

  const needed50M = Math.max(0, 50_000_000 - totalARR);

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="page-header mb-0">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-navy/10 flex items-center justify-center">
            <Building2 size={16} className="text-navy" />
          </div>
          <div>
            <h1 className="page-title">Business Case</h1>
            <p className="page-subtitle">Market opportunity, revenue model, partner economics, and the MRN competitive flywheel</p>
          </div>
        </div>
        <div className="mt-1">
          <span className="badge badge-red text-2xs">Internal Only — MRN Admin</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
        {([
          { id: "market",   label: "Market Opportunity"   },
          { id: "revenue",  label: "Revenue Model"        },
          { id: "partners", label: "Partner Value Story"  },
          { id: "flywheel", label: "The MRN Flywheel"     },
          { id: "identity", label: "Identity Advantage"   },
        ] as { id: Tab; label: string }[]).map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={clsx("px-4 py-2.5 text-sm font-medium border-b-2 transition-all duration-150 -mb-px whitespace-nowrap",
              tab === t.id ? "border-blue text-blue" : "border-transparent text-slate hover:text-navy")}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Market Opportunity ── */}
      {tab === "market" && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: "Global Retail Media Market",    value: "$100B+",  sub: "+22% YoY growth",          badge: "badge-green", color: "text-navy"  },
              { label: "Open Programmatic — Addressable",value: "$48B",   sub: "Excluding walled gardens", badge: "badge-blue",  color: "text-blue"  },
              { label: "MRN Platform Fee TAM",          value: "$4.8B",   sub: "$48B × 10% avg fee",       badge: "badge-cyan",  color: "text-cyan"  },
            ].map((kpi, i) => (
              <div key={i} className="kpi-card text-center">
                <p className="kpi-label">{kpi.label}</p>
                <p className={clsx("text-3xl font-black mt-2 mb-1", kpi.color)}>{kpi.value}</p>
                <span className={clsx("badge", kpi.badge)}>{kpi.sub}</span>
              </div>
            ))}
          </div>

          {/* Vertical bar chart */}
          <div className="card">
            <h3 className="text-sm font-semibold text-navy mb-1">Addressable Market by Vertical</h3>
            <p className="text-xs text-slate mb-4">Partner count × avg spend × real MRN intent signal volume per category</p>
            <div className="space-y-3">
              {VERTICALS.map((v, i) => {
                const tam = v.partners * v.avgSpend;
                const maxTam = Math.max(...VERTICALS.map(x => x.partners * x.avgSpend));
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs font-semibold text-gray-800 truncate">{v.name}</span>
                        <span className="badge badge-slate text-2xs flex-shrink-0">{v.partners} partners</span>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="text-2xs text-cyan tabular">{fmt(v.signals)} signals/wk</span>
                        <span className="text-xs font-bold text-navy tabular">{fmt(tam, "currency")}</span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2.5">
                      <div className="h-2.5 rounded-full bg-gradient-to-r from-navy to-blue transition-all duration-700"
                        style={{ width: `${(tam / maxTam) * 100}%` }} />
                    </div>
                    <p className="text-2xs text-slate mt-0.5">Avg spend {fmt(v.avgSpend, "currency")}/partner · {v.category} intent</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Programmatic value chain */}
          <div className="card bg-gradient-to-br from-navy/5 to-blue/5 border border-navy/10">
            <h3 className="text-sm font-semibold text-navy mb-4">The Programmatic Value Chain — Where MRN Sits</h3>
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {[
                { label: "Brand Budget",     sub: "Advertiser",      highlight: false },
                { label: "Agency",           sub: "Planning & Buy",   highlight: false },
                { label: "DSP",              sub: "Execution",        highlight: false },
                { label: "MRN",              sub: "Intelligence Layer",highlight: true  },
                { label: "Publisher",        sub: "Inventory",        highlight: false },
                { label: "Consumer",         sub: "End Audience",     highlight: false },
              ].map((node, i) => (
                <React.Fragment key={i}>
                  <div className={clsx(
                    "flex-1 min-w-[90px] rounded-xl p-3 text-center border-2 transition-all",
                    node.highlight
                      ? "bg-blue border-blue shadow-lg shadow-blue/20 scale-105"
                      : "bg-white border-gray-200"
                  )}>
                    <p className={clsx("text-xs font-bold", node.highlight ? "text-white" : "text-navy")}>{node.label}</p>
                    <p className={clsx("text-2xs mt-0.5", node.highlight ? "text-white/70" : "text-slate")}>{node.sub}</p>
                    {node.highlight && (
                      <div className="mt-1.5 bg-white/20 rounded-full px-2 py-0.5">
                        <p className="text-2xs text-white font-semibold">8–15% fee</p>
                      </div>
                    )}
                  </div>
                  {i < 5 && <ArrowRight size={16} className="text-gray-300 flex-shrink-0" />}
                </React.Fragment>
              ))}
            </div>
            <div className="mt-4 bg-blue/10 rounded-lg p-3 border border-blue/20">
              <p className="text-xs font-semibold text-blue mb-1">MRN captures 8–15% platform fee on every dollar flowing through the intelligence layer.</p>
              <p className="text-xs text-slate">MRN is the toll booth on the programmatic highway. Every campaign that uses MRN Person IDs, MRN intent signals, or MRN suppression pays a platform fee — regardless of which DSP executes the buy.</p>
            </div>
          </div>
        </div>
      )}

      {/* ── Revenue Model ── */}
      {tab === "revenue" && (
        <div className="grid grid-cols-12 gap-4">
          {/* Sliders */}
          <div className="col-span-5 space-y-3">
            <div className="card">
              <p className="text-xs font-bold text-navy mb-3 flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-navy inline-block" /> Loyalty Intelligence
              </p>
              <div className="space-y-2">
                <Slider label="Partners" value={loyaltyCount} min={0} max={20} step={1} format="number" onChange={setLoyaltyCount} />
                <Slider label="Avg ACV" value={loyaltyACV} min={150_000} max={500_000} step={25_000} format="currency" onChange={setLoyaltyACV} />
              </div>
              <p className="text-xs font-semibold text-navy mt-2 text-right">{fmt(loyaltyRev, "currency")}</p>
            </div>
            <div className="card">
              <p className="text-xs font-bold text-blue mb-3 flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-blue inline-block" /> Full-Funnel Activation
              </p>
              <div className="space-y-2">
                <Slider label="Partners" value={activationCount} min={0} max={15} step={1} format="number" onChange={setActivationCount} />
                <Slider label="Avg Managed Spend" value={activationSpend} min={500_000} max={5_000_000} step={250_000} format="currency" onChange={setActivationSpend} />
                <Slider label="Platform Fee %" value={activationFee} min={8} max={15} step={0.5} format="percent" onChange={setActivationFee} />
              </div>
              <p className="text-xs font-semibold text-blue mt-2 text-right">{fmt(activationRev, "currency")}</p>
            </div>
            <div className="card">
              <p className="text-xs font-bold mb-3" style={{ color: "#0891B2" }} >
                <span className="w-3 h-3 rounded-full inline-block mr-2" style={{ backgroundColor: "#0891B2" }} />
                Enterprise Suite
              </p>
              <div className="space-y-2">
                <Slider label="Partners" value={enterpriseCount} min={0} max={10} step={1} format="number" onChange={setEnterpriseCount} />
                <Slider label="Avg ACV" value={enterpriseACV} min={1_000_000} max={3_000_000} step={100_000} format="currency" onChange={setEnterpriseACV} />
              </div>
              <p className="text-xs font-semibold mt-2 text-right" style={{ color: "#0891B2" }}>{fmt(enterpriseRev, "currency")}</p>
            </div>
            <div className="card">
              <p className="text-xs font-bold text-slate mb-3">Other Revenue Streams</p>
              <div className="space-y-2">
                <Slider label="Suppression partners" value={suppCount} min={0} max={20} step={1} format="number" onChange={setSuppCount} />
                <Slider label="Suppression ACV" value={suppACV} min={50_000} max={150_000} step={10_000} format="currency" onChange={setSuppACV} />
                <Slider label="White-Label partners" value={wlCount} min={0} max={3} step={1} format="number" onChange={setWlCount} />
                <Slider label="White-Label ACV" value={wlACV} min={2_000_000} max={10_000_000} step={500_000} format="currency" onChange={setWlACV} />
                <Slider label="O&O CPM ($)" value={ooCPM} min={12} max={25} step={1} format="currency" onChange={setOoCPM} />
                <Slider label="O&O Sell-Through" value={ooSellThrough} min={40} max={80} step={5} format="percent" onChange={setOoSellThrough} />
              </div>
              <p className="text-xs font-semibold text-amber-600 mt-2 text-right">O&O: {fmt(ooRev, "currency")}</p>
            </div>
          </div>

          {/* Results */}
          <div className="col-span-7 space-y-4">
            {/* Hero ARR */}
            <div className="card bg-gradient-to-br from-navy to-navy/90 text-center py-6">
              <p className="text-white/60 text-xs uppercase tracking-widest mb-2">Total Annual Recurring Revenue</p>
              <p className="text-5xl font-black text-white tabular">{fmt(totalARR, "currency")}</p>
              <p className="text-white/40 text-xs mt-2">ARR at current partner mix</p>
            </div>

            {/* Revenue breakdown */}
            <div className="grid grid-cols-2 gap-4">
              <div className="card">
                <h3 className="text-xs font-semibold text-navy mb-3">Revenue by Stream</h3>
                <div className="space-y-2">
                  {revenueStreams.filter(s => s.value > 0).map((s, i) => (
                    <div key={i}>
                      <div className="flex justify-between mb-0.5">
                        <span className="text-2xs text-slate">{s.name}</span>
                        <span className="text-2xs font-semibold tabular" style={{ color: s.color }}>{fmt(s.value, "currency")}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-1.5">
                        <div className="h-1.5 rounded-full" style={{ width: `${(s.value/totalARR)*100}%`, backgroundColor: s.color }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="card flex flex-col items-center justify-center">
                <h3 className="text-xs font-semibold text-navy mb-2 self-start">Revenue Mix</h3>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={revenueStreams.filter(s => s.value > 0)} cx="50%" cy="50%" outerRadius={65}
                      dataKey="value" nameKey="name">
                      {revenueStreams.filter(s => s.value > 0).map((s, i) => <Cell key={i} fill={s.color} />)}
                    </Pie>
                    <Tooltip formatter={(v: number) => [fmt(v, "currency"), ""]} contentStyle={{ fontSize: 11, borderRadius: 6 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* 3-year projection */}
            <div className="card">
              <h3 className="text-xs font-semibold text-navy mb-3">3-Year ARR Projection (60% YoY growth)</h3>
              <ResponsiveContainer width="100%" height={140}>
                <BarChart data={yearlyProjection} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="year" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false} unit="M" />
                  <Tooltip formatter={(v: number) => [`$${v}M`, "ARR"]} contentStyle={{ fontSize: 11, borderRadius: 6 }} />
                  <Bar dataKey="arr" fill="#3056D3" radius={[4,4,0,0]} name="ARR ($M)" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Path to $50M */}
            <div className={clsx("rounded-lg p-3 border", totalARR >= 50_000_000 ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200")}>
              <p className={clsx("text-xs font-bold mb-1", totalARR >= 50_000_000 ? "text-emerald-700" : "text-amber-700")}>
                {totalARR >= 50_000_000 ? "✓ $50M ARR Target Achieved" : `Path to $50M ARR — ${fmt(needed50M, "currency")} remaining`}
              </p>
              <p className="text-xs text-slate">
                Current mix: {loyaltyCount} Loyalty ({fmt(loyaltyRev, "currency")}) + {activationCount} Activation ({fmt(activationRev, "currency")}) + {enterpriseCount} Enterprise ({fmt(enterpriseRev, "currency")}) + O&O ({fmt(ooRev, "currency")})
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Partner Value Story ── */}
      {tab === "partners" && (
        <div className="space-y-4">
          {RETAILERS.map(retailer => {
            const summary = LOYALTY_SUMMARIES.find(s => s.retailerId === retailer.id)!;
            const mrnFeeEst   = retailer.loyaltyMembers * 0.012;
            const retentionVal = summary.lapseRiskCount * 84;
            const conquestVal  = summary.conquestProspects * 4.20;
            const suppVal      = retailer.loyaltyMembers * 0.008 * 0.15;
            const totalVal     = retentionVal + conquestVal + suppVal;
            const partnerROI   = totalVal / mrnFeeEst;
            return (
              <div key={retailer.id} className="card border border-navy/10">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-navy/10 flex items-center justify-center">
                      <Building2 size={18} className="text-navy" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-navy">{retailer.name}</h3>
                      <p className="text-xs text-slate">{retailer.vertical} · {fmt(retailer.loyaltyMembers)} loyalty members</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xs text-slate">Partner ROI</p>
                    <p className="text-2xl font-black text-emerald-600">{partnerROI.toFixed(1)}x</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  {/* Before */}
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs font-bold text-slate mb-2 flex items-center gap-1.5">
                      <XCircle size={13} className="text-red-400" /> Before MRN
                    </p>
                    <div className="space-y-1.5">
                      {[
                        "Match rate: ~25% — 3 in 4 customers unreachable",
                        "No intent visibility — blind to what members want next",
                        "No cross-device — desktop, mobile, CTV all separate",
                        "No suppression — wasting spend on recent converters",
                        "Last-click attribution — guessing what worked",
                      ].map((item, i) => (
                        <div key={i} className="flex items-start gap-1.5">
                          <span className="text-red-400 text-xs mt-0.5 flex-shrink-0">✗</span>
                          <p className="text-xs text-slate">{item}</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-2xs text-slate/60 mt-2 italic">Backward-looking. You only know what already happened.</p>
                  </div>
                  {/* After */}
                  <div className="bg-emerald-50/50 rounded-lg p-3 border border-emerald-100">
                    <p className="text-xs font-bold text-emerald-700 mb-2 flex items-center gap-1.5">
                      <CheckCircle size={13} className="text-emerald-500" /> With MRN
                    </p>
                    <div className="space-y-1.5">
                      {[
                        `Match rate: ${retailer.matchRate}% — MRN Super Identity Graph`,
                        "Intent overlay on all members — daily refresh, 8,305 topics",
                        "5.88B mobile + 17.4B CTV — true cross-device identity",
                        "Real-time suppression — stop wasting on converters",
                        "Person-level closed-loop — deterministic proof",
                      ].map((item, i) => (
                        <div key={i} className="flex items-start gap-1.5">
                          <span className="text-emerald-500 text-xs mt-0.5 flex-shrink-0">✓</span>
                          <p className="text-xs text-gray-700">{item}</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-2xs text-emerald-700/60 mt-2 italic">Forward-looking. You know what's about to happen.</p>
                  </div>
                </div>

                {/* Financial impact */}
                <div className="grid grid-cols-4 gap-3">
                  {[
                    { label: "Lapse Risk Members",    value: fmt(summary.lapseRiskCount),  sub: "identified", detail: `~${fmt(retentionVal, "currency")} retention value` },
                    { label: "Conquest Prospects",    value: fmt(summary.conquestProspects),sub: "found",      detail: `~${fmt(conquestVal, "currency")} acquisition value` },
                    { label: "Suppression Savings",   value: "15–20%",                     sub: "of spend",   detail: "recovered from wasted impressions" },
                    { label: "Attribution",           value: "100%",                        sub: "deterministic",detail: "from probabilistic guessing to proof" },
                  ].map((m, i) => (
                    <div key={i} className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-lg font-black text-navy tabular">{m.value}</p>
                      <p className="text-2xs text-slate">{m.sub}</p>
                      <p className="text-2xs font-semibold text-navy mt-1">{m.label}</p>
                      <p className="text-2xs text-slate/70 mt-0.5">{m.detail}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-3 bg-navy rounded-lg p-3 flex items-center justify-between">
                  <p className="text-white text-xs">
                    Total annual value to <span className="font-bold">{retailer.name}</span>:{" "}
                    <span className="text-emerald-400 font-bold">{fmt(totalVal, "currency")}</span>
                    {" "}· MRN fee: <span className="text-white/70">{fmt(mrnFeeEst, "currency")}</span>
                  </p>
                  <span className="text-emerald-400 font-black text-lg flex-shrink-0">{partnerROI.toFixed(1)}x ROI</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Flywheel ── */}
      {tab === "flywheel" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Flywheel SVG */}
            <div className="card flex flex-col items-center">
              <h3 className="text-sm font-semibold text-navy mb-4 self-start">The MRN Compounding Flywheel</h3>
              <svg width={320} height={320} viewBox="0 0 320 320">
                {/* Center */}
                <circle cx={160} cy={160} r={52} fill="#1E2761" />
                <text x={160} y={154} textAnchor="middle" fill="white" fontSize={11} fontWeight={700} fontFamily="Inter, sans-serif">MRN</text>
                <text x={160} y={168} textAnchor="middle" fill="white" fontSize={9} fontFamily="Inter, sans-serif">Super Identity</text>
                <text x={160} y={180} textAnchor="middle" fill="white" fontSize={9} fontFamily="Inter, sans-serif">Graph</text>

                {/* Outer ring */}
                <circle cx={160} cy={160} r={130} fill="none" stroke="#e5e7eb" strokeWidth={1} strokeDasharray="4 4" />

                {/* Stage nodes */}
                {FLYWHEEL.map((stage, i) => {
                  const rad = (stage.angle * Math.PI) / 180;
                  const x   = 160 + 130 * Math.cos(rad);
                  const y   = 160 + 130 * Math.sin(rad);
                  return (
                    <g key={i}>
                      <circle cx={x} cy={y} r={22} fill={stage.color} />
                      <text x={x} y={y + 4} textAnchor="middle" fill="white" fontSize={10} fontWeight={700} fontFamily="Inter, sans-serif">{stage.id}</text>
                      {/* Arrow from center */}
                      <line x1={160 + 52 * Math.cos(rad)} y1={160 + 52 * Math.sin(rad)}
                        x2={x - 22 * Math.cos(rad)}     y2={y - 22 * Math.sin(rad)}
                        stroke={stage.color} strokeWidth={1.5} strokeDasharray="3 2" opacity={0.5} />
                    </g>
                  );
                })}
              </svg>

              {/* Legend */}
              <div className="grid grid-cols-2 gap-2 w-full mt-2">
                {FLYWHEEL.map((s, i) => (
                  <div key={i} className="flex items-start gap-1.5">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-white text-2xs font-bold mt-0.5"
                      style={{ backgroundColor: s.color }}>{s.id}</div>
                    <div>
                      <p className="text-2xs font-semibold text-gray-800">{s.label}</p>
                      <p className="text-2xs text-slate leading-tight">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Network density */}
            <div className="space-y-4">
              <div className="card">
                <h3 className="text-sm font-semibold text-navy mb-1">Network Density Effect</h3>
                <p className="text-xs text-slate mb-3">Match rate and ROAS improve as partner count grows</p>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={NETWORK_DATA} margin={{ top: 4, right: 16, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="partners" tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false} label={{ value: "Partners", position: "insideBottom", offset: -2, fontSize: 10 }} />
                    <YAxis yAxisId="left"  tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false} domain={[85, 100]} unit="%" />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false} domain={[4, 7]} unit="x" />
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6, border: "1px solid #e5e7eb" }} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                    <Line yAxisId="left"  type="monotone" dataKey="matchRate" stroke="#1E2761" strokeWidth={2.5} dot={{ fill: "#1E2761", r: 4 }} name="Match Rate %" />
                    <Line yAxisId="right" type="monotone" dataKey="roas"      stroke="#0891B2" strokeWidth={2.5} dot={{ fill: "#0891B2", r: 4 }} name="Avg ROAS" />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="card bg-blue/5 border border-blue/20">
                <div className="flex items-center gap-2 mb-2">
                  <Zap size={14} className="text-blue" />
                  <p className="text-xs font-bold text-navy">The Durable Competitive Moat</p>
                </div>
                <p className="text-xs text-slate leading-relaxed">
                  Every new partner makes the MRN platform more valuable for every existing partner. This data network effect — match rates improving, ROAS climbing, signals compounding — is impossible to replicate without the graph. The asset grows more valuable every single day.
                </p>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  {[
                    { label: "Perpetual data license", sub: "Asset compounds over time" },
                    { label: "Network density effect",  sub: "More partners = better results for all" },
                    { label: "Daily intent refresh",    sub: "1-day latency vs. 30-90 day norm" },
                    { label: "Identity compounding",    sub: "Every campaign adds match density" },
                  ].map((m, i) => (
                    <div key={i} className="bg-white rounded-lg p-2 border border-blue/10">
                      <p className="text-2xs font-semibold text-navy">{m.label}</p>
                      <p className="text-2xs text-slate">{m.sub}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Identity Advantage ── */}
      {tab === "identity" && (
        <div className="space-y-4">
          {/* Hero */}
          <div className="bg-dark rounded-xl p-6 text-center border border-white/5">
            <p className="text-white/40 text-xs uppercase tracking-widest mb-2">The Foundation of Everything</p>
            <h2 className="text-white text-2xl font-black mb-1">The MRN Super Identity Graph</h2>
            <p className="text-white/50 text-sm">Every insight starts here. Every campaign runs through here. Every conversion traces back here.</p>
          </div>

          {/* Animated stat row */}
          <div className="grid grid-cols-6 gap-3">
            {[
              { label: "Individuals",    value: "498M",   color: "bg-navy/10 text-navy"  },
              { label: "Mobile Devices", value: "5.88B",  color: "bg-blue/10 text-blue"  },
              { label: "CTV Screens",    value: "23.4B",  color: "bg-navy/10 text-navy"  },
              { label: "IP Addresses",   value: "14.6B",  color: "bg-cyan/10 text-cyan"  },
              { label: "Email Records",  value: "1.74B",  color: "bg-blue/10 text-blue"  },
              { label: "Phone Numbers",  value: "899M",   color: "bg-cyan/10 text-cyan"  },
            ].map((s, i) => (
              <div key={i} className={clsx("rounded-xl p-3 text-center", s.color.split(" ")[0])}>
                <p className={clsx("text-xl font-black tabular", s.color.split(" ")[1])}>{s.value}</p>
                <p className="text-2xs text-slate mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>

          {/* How MRN works in real time */}
          <div className="card">
            <h3 className="text-sm font-semibold text-navy mb-4">How MRN Works in Real Time — O&O Property Visit</h3>
            <div className="flex items-start gap-3 overflow-x-auto pb-2">
              {[
                { step: 1, label: "Visitor Arrives",          desc: "Someone lands on a McClatchy O&O property",                                        color: "bg-gray-100",   text: "text-gray-700" },
                { step: 2, label: "Identity Resolved",         desc: "MRN matches to a persistent MRN Person ID in milliseconds",                         color: "bg-blue/10",    text: "text-blue"     },
                { step: 3, label: "Full Profile Activated",    desc: "2,167 attributes, active intent signals, device graph, financial propensity",        color: "bg-navy/10",    text: "text-navy"     },
                { step: 4, label: "Precision Ad Served",       desc: "Right message, right person, right moment — on any device",                         color: "bg-cyan/10",    text: "text-cyan"     },
                { step: 5, label: "Conversion Tracked",        desc: "Linked back to this MRN Person ID — online, in-store, across any device",           color: "bg-emerald-50", text: "text-emerald-700"},
              ].map((s, i) => (
                <React.Fragment key={i}>
                  <div className={clsx("flex-1 min-w-[140px] rounded-xl p-3 border border-gray-100", s.color)}>
                    <div className={clsx("w-6 h-6 rounded-full bg-white border border-gray-200 flex items-center justify-center text-xs font-bold mb-2", s.text)}>{s.step}</div>
                    <p className={clsx("text-xs font-bold mb-1", s.text)}>{s.label}</p>
                    <p className="text-2xs text-slate leading-relaxed">{s.desc}</p>
                  </div>
                  {i < 4 && <ArrowRight size={16} className="text-gray-300 flex-shrink-0 mt-8" />}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Match rate comparison */}
          <div className="grid grid-cols-2 gap-4">
            <div className="card">
              <h3 className="text-sm font-semibold text-navy mb-4">Match Rate — Industry vs. MRN</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <p className="text-3xl font-black text-gray-300 tabular">25%</p>
                  <p className="text-xs text-slate mt-1">Without MRN</p>
                  <p className="text-2xs text-slate/60 mt-2">Industry average identity match rate. 3 in 4 customers are invisible.</p>
                  <div className="w-full bg-gray-100 rounded-full h-3 mt-3">
                    <div className="bg-gray-300 h-3 rounded-full" style={{ width: "25%" }} />
                  </div>
                </div>
                <div className="text-center p-4 bg-blue/5 rounded-xl border border-blue/20">
                  <p className="text-3xl font-black text-blue tabular">92%</p>
                  <p className="text-xs text-blue mt-1">With MRN</p>
                  <p className="text-2xs text-slate mt-2">MRN Super Identity Graph. 9 in 10 customers addressable.</p>
                  <div className="w-full bg-gray-100 rounded-full h-3 mt-3">
                    <div className="bg-gradient-to-r from-blue to-cyan h-3 rounded-full" style={{ width: "92%" }} />
                  </div>
                </div>
              </div>
              <div className="mt-3 bg-blue/5 rounded-lg p-3 text-center border border-blue/10">
                <p className="text-sm font-bold text-blue">3.7x more addressable consumers from the same customer file.</p>
              </div>
            </div>

            <div className="card bg-dark border border-white/5">
              <p className="text-white/40 text-xs uppercase tracking-widest mb-3">The Compounding Asset</p>
              <p className="text-white text-sm leading-relaxed mb-4">
                The MRN identity graph is licensed <span className="text-cyan font-semibold">perpetually</span>. It compounds with every campaign, every partner, every signal.
              </p>
              <div className="space-y-2">
                {[
                  { label: "Every campaign adds attribution data",      icon: "→" },
                  { label: "Every partner adds match density",          icon: "→" },
                  { label: "Every signal refines intent models",        icon: "→" },
                  { label: "Every conversion improves predictions",     icon: "→" },
                  { label: "The asset grows more valuable every day",   icon: "★" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className={clsx("text-xs flex-shrink-0", i === 4 ? "text-cyan" : "text-white/30")}>{item.icon}</span>
                    <p className={clsx("text-xs", i === 4 ? "text-cyan font-semibold" : "text-white/60")}>{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}